/**
 * GroupedBarChart — responsive SVG bar chart with up to 2 series per group.
 * Used by Admin "Submission Activity" chart.
 *
 * Flow: compute bar geometry → build SVG rects + axis labels.
 * RTL: labels + bar groups are rendered in reverse order when `isRTL` so latest month
 * sits at the visual start (right side of the chart in RTL).
 * Colors via CSS variable strings → dark mode automatic.
 */
import { useLayoutEffect, useRef, useState } from "react";
import { useDirection } from "@/shared/hooks/useDirection";
import { useLocale } from "@/shared/hooks/useLocale";

export interface BarSeries {
  label: string;
  color: string;
  values: number[];
}

interface GroupedBarChartProps {
  labels: string[];
  series: BarSeries[];
  height?: number;
  /** Fill parent container's height (measures container; container must have explicit height). */
  fill?: boolean;
  /** Used to explicitly force RTL in tests / Storybook. */
  forceRtl?: boolean;
}

const W = 560;

export function GroupedBarChart({ labels, series, height: heightProp = 168, fill, forceRtl }: GroupedBarChartProps) {
  const { isRTL } = useDirection();
  const { fmtNum } = useLocale();
  const rtl = forceRtl ?? isRTL;

  const containerRef = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<{ w: number; h: number }>({ w: W, h: heightProp });

  // Always measure width so the viewBox matches the card's real width (no
  // preserveAspectRatio centering gap). Height only tracked in `fill` mode.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0) setMeasured({ w, h: fill && h > 0 ? h : heightProp });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [fill, heightProp]);

  const height = fill ? measured.h : heightProp;
  const width  = measured.w;

  const pL = 22, pR = 8, pT = 10, pB = 28;
  const cW = width - pL - pR;
  const cH = height - pT - pB;
  const n = labels.length;
  const allVals = series.flatMap((s) => s.values);
  const yMax = Math.ceil((Math.max(...allVals, 1) / 5) * 5 + 4);
  const ticks = 4;

  const orderedLabels = rtl ? [...labels].reverse() : labels;
  const orderedSeries = series.map((s) => ({
    ...s,
    values: rtl ? [...s.values].reverse() : s.values,
  }));

  const gW = cW / n;
  const bW = Math.min(24, gW * 0.3);
  const gap = bW * 0.4;

  const gridLines = Array.from({ length: ticks + 1 }, (_, i) => {
    const v = Math.round(((ticks - i) / ticks) * yMax);
    const y = pT + (i / ticks) * cH;
    return { v, y };
  });

  return (
    <div ref={containerRef} className={fill ? "h-full w-full" : "w-full"}>
    <svg
      width="100%"
      height={fill ? "100%" : height}
      viewBox={`0 0 ${width} ${height}`}
      {...(fill && { preserveAspectRatio: "none" })}
    >
      {/* Grid + y-labels */}
      {gridLines.map(({ v, y }) => (
        <g key={y}>
          <line x1={pL} y1={y} x2={width - pR} y2={y} stroke="var(--border-light)" strokeWidth="1" />
          <text
            x={pL - 6} y={y + 3.5}
            textAnchor="end" fontSize="9"
            fill="var(--text-tertiary)" fontFamily="var(--font-sans)"
          >
            {fmtNum(v)}
          </text>
        </g>
      ))}

      {/* Bars + x-labels */}
      {orderedLabels.map((lbl, i) => {
        const gx = pL + i * gW + gW / 2;
        return (
          <g key={i}>
            {orderedSeries.map((s, si) => {
              const val = s.values[i] ?? 0;
              const bH = (val / yMax) * cH;
              const seriesCount = orderedSeries.length;
              const totalW = seriesCount * bW + (seriesCount - 1) * gap;
              const x = gx - totalW / 2 + si * (bW + gap);
              return (
                <rect
                  key={si}
                  x={x} y={pT + cH - bH}
                  width={bW} height={bH}
                  rx="3" fill={s.color} opacity="0.75"
                />
              );
            })}
            <text
              x={gx} y={height - pB + 16}
              textAnchor="middle" fontSize="9"
              fill="var(--text-tertiary)" fontFamily="var(--font-sans)"
            >
              {lbl}
            </text>
          </g>
        );
      })}
    </svg>
    </div>
  );
}
